<template>
  <div
    v-if="visible"
    class="container">
    <div
      class="shared-ride-warning"
      :dir="isHebrew ? 'rtl' : 'ltr'"
      role="alert">
      <h5 class="shared-ride-warning-title">⚠️ {{ t('sharedRideWarningTitle') }}</h5>
      <p class="shared-ride-warning-text">
        {{ t('sharedRideWarningSentTo') }} <strong>{{ customerNames }}</strong>
      </p>
      <p class="shared-ride-warning-text">{{ t('sharedRideWarningBody') }}</p>
      <div class="shared-ride-warning-actions">
        <b-btn
          variant="outline-secondary"
          @click="dismissed = true">
          {{ t('sharedRideWarningSameCustomer') }}
        </b-btn>
        <b-btn
          variant="warning"
          @click="duplicate">
          {{ t('sharedRideWarningNewCustomer') }}
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import manageRidePrivacyApi from '@/api/manageRidePrivacy';
import {
  canOfferSharedRideWarning,
  sharedRideCustomers,
  customerNamesSummary,
} from '@/utils/sharedRideWarning';

// Asks a ride manager who opens a trip customers already have whether the
// change is for the same customer, and offers to duplicate it if not.
// Once per visit: answering hides it until the ride page is opened again.
export default {
  name: 'SharedRideWarning',
  mixins: [translationMixin],
  data() {
    return {
      customers: [],
      dismissed: false,
    };
  },
  computed: {
    ...mapGetters(['apptoken', 'email', 'userId', 'rideHeader', 'compareToUserId', 'isRideLocked']),
    ride() {
      return this.rideHeader && this.rideHeader.ride;
    },
    canOffer() {
      return canOfferSharedRideWarning({
        ride: this.ride,
        userId: this.userId,
        isOwner: !!this.ride && this.compareToUserId(this.ride.authorId),
        isLocked: this.isRideLocked,
      });
    },
    visible() {
      return this.canOffer && !this.dismissed && this.customers.length > 0;
    },
    customerNames() {
      const { names, more } = customerNamesSummary(this.customers);
      const listed = names.join(', ');
      if (!more) return listed;
      return this.isHebrew
        ? `${listed} ${this.t('sharedRideWarningMore')} ${more}`
        : `${listed} +${more} ${this.t('sharedRideWarningMore')}`;
    },
  },
  watch: {
    // The ride page is reused when moving from one ride to another, so a new
    // ride is a new visit.
    'ride.id': {
      immediate: true,
      handler() {
        this.dismissed = false;
        this.customers = [];
        this.fetchCustomers();
      },
    },
    canOffer(newVal) {
      if (newVal && !this.customers.length) this.fetchCustomers();
    },
  },
  methods: {
    ...mapActions(['copyPrivateRide']),
    async fetchCustomers() {
      if (!this.canOffer) return;
      const rideId = this.ride.id;
      try {
        const res = await manageRidePrivacyApi.getRideConnectedUsers(this.apptoken, rideId);
        // The manager may have moved to another ride while this was loading.
        if (!this.ride || this.ride.id !== rideId) return;
        this.customers = sharedRideCustomers(res.data.users, this.email);
      } catch (e) {
        // Without the list we cannot tell, and the warning is a hint, not a gate.
        this.customers = [];
      }
    },
    duplicate() {
      this.dismissed = true;
      this.copyPrivateRide(this.ride.id);
    },
  },
};
</script>

<style scoped>
.shared-ride-warning {
  margin: 16px 0;
  padding: 16px 20px;
  border: 2px solid #f0ad4e;
  border-radius: 10px;
  background: #fff8e6;
  color: #5c4400;
}

.shared-ride-warning-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.shared-ride-warning-text {
  font-size: 15px;
  margin-bottom: 6px;
}

.shared-ride-warning-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.shared-ride-warning-actions .btn {
  font-size: 15px;
}
</style>
